import { useEffect, useRef } from "react"
import { ArrowLeft, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"

import { Button } from "@/shared/components/ui/button"
import { useConfigReturn } from "./config-return-context"

const normalizePath = (path: string) =>
  path.split(/[?#]/)[0].replace(/\/$/, "")

/**
 * Fixed top-right pill that appears after a user follows an "Edit config" link
 * into a config page, offering a one-click way back to the surface they came
 * from. Mounted once at the app root so it survives navigation. Shows only when
 * a return target is set AND the user has navigated away from it.
 */
export function ConfigReturnBanner() {
  const { t } = useTranslation()
  const { target, clear } = useConfigReturn()
  const navigate = useNavigate()
  const location = useLocation()

  // The banner is a consequence of one specific redirect. Once the user leaves
  // the destination they were redirected to, dismiss it for good. We wait until
  // the destination has actually been reached before arming the dismissal, so
  // the batched set-target + navigate that opens the banner never trips it.
  const currentPath = normalizePath(location.pathname)
  const destinationPath = target ? normalizePath(target.destination) : null
  const arrivedRef = useRef(false)
  useEffect(() => {
    if (!target) {
      arrivedRef.current = false
      return
    }
    if (currentPath === destinationPath) {
      arrivedRef.current = true
    } else if (arrivedRef.current) {
      arrivedRef.current = false
      clear()
    }
  }, [target, currentPath, destinationPath, clear])

  if (!target) return null
  // Hide while the user is still on (or back on) the originating surface.
  // Normalize trailing slashes so "/x" and "/x/" are treated as equal.
  const targetPath = normalizePath(target.path)
  if (currentPath === targetPath) return null

  return (
    <div className="fixed right-4 top-16 z-40 flex items-center gap-2 rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] py-1 pl-1 pr-2 shadow-[var(--shadow-float)]">
      <Button
        variant="ghost"
        size="xs"
        className="rounded-full text-[var(--green)] hover:bg-[color-mix(in_srgb,var(--green)_10%,transparent)]"
        onClick={() => {
          const dest = target.path
          clear()
          navigate(dest)
        }}
      >
        <ArrowLeft aria-hidden />
        {t("configHelp.returnTo", {
          defaultValue: "Return to {{label}}",
          label: target.label,
        })}
      </Button>
      <Button
        variant="ghost"
        size="icon-xs"
        className="rounded-full text-[var(--ink-3)] hover:bg-[color-mix(in_srgb,var(--green)_10%,transparent)]"
        aria-label={t("configHelp.dismissReturn", { defaultValue: "Dismiss" })}
        title={t("configHelp.dismissReturn", { defaultValue: "Dismiss" })}
        onClick={clear}
      >
        <X aria-hidden />
      </Button>
    </div>
  )
}
